import { useMemo } from 'react'

import { useOpenRule } from '../hooks/useOpenRule'
import { useRemoveRule } from '../hooks/useRemoveRule'
import { useUpdateRule } from '../hooks/useUpdateRule'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { Box as ChakraBox } from '@chakra-ui/react'

import { Flex, Table, TableBody, TablePagination, Text } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import {
  RULES_TABLE_EMPTY_ROW_COUNT,
  RULES_TABLE_HEADER_HEIGHT,
  RULES_TABLE_MAX_HEIGHT,
  RULES_TABLE_MAX_WIDTH,
  RULES_TABLE_PAGE_SIZE_OPTIONS,
  RULES_TABLE_PAGINATION_HEIGHT,
  RULES_TABLE_ROW_HEIGHT,
} from '../constants/layout'
import type { RuleSortColumn, RuleSortOrder } from '../types/rulesTable'
import { getRuleRows } from '../utils/ruleRows'
import { getRulesTableToken } from '../utils/theme'
import type { RulesListType, RulesType } from '../utils/utils'
import { ArchiveRuleModal } from './ArchiveRuleModal'
import { useDarkRulesColumns } from './DarkRulesColumns'
import { DarkRulesTableHeader } from './DarkRulesTableHeader'
import { DarkRulesTableRow } from './DarkRulesTableRow'

interface DarkRulesTableIProps {
  colorScheme: ColorScheme
  isLoading?: boolean
  pageIndex: number
  pageSize: number
  rules: RulesListType
  sortColumn: RuleSortColumn | null
  sortOrder: RuleSortOrder | null
  totalCount?: number
  onPageChange: (pageIndex: number) => void
  onPageSizeChange: (pageSize: number) => void
  onSortChange: (column: RuleSortColumn, order: RuleSortOrder) => void
}

export const DarkRulesTable = ({
  colorScheme,
  isLoading = false,
  pageIndex,
  pageSize,
  rules,
  sortColumn,
  sortOrder,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  onSortChange,
}: DarkRulesTableIProps) => {
  const { t } = useTranslation()
  const { openRule } = useOpenRule()
  const { shouldEnableEditIncidentRule } = usePermissions()
  const { updateRuleStatus } = useUpdateRule()
  const {
    isArchiveRuleLoading,
    isOpen,
    onClose,
    onOpen,
    setSelectedRule,
    handleRemoveRule,
    selectedRule,
  } = useRemoveRule()

  const rows = useMemo(() => getRuleRows(rules, t), [rules, t])
  const pageCount = Math.ceil(totalCount / pageSize)
  const displayedRowCount = isLoading
    ? pageSize
    : Math.max(rows.length, RULES_TABLE_EMPTY_ROW_COUNT)
  const tableHeight =
    RULES_TABLE_HEADER_HEIGHT +
    displayedRowCount * RULES_TABLE_ROW_HEIGHT +
    RULES_TABLE_PAGINATION_HEIGHT

  const handleToggleRule = async (rule: RulesType) => {
    await updateRuleStatus(rule)
  }

  const handleArchiveRule = (rule: RulesType) => {
    if (!shouldEnableEditIncidentRule || rule?.status) return
    setSelectedRule(rule)
    onOpen()
  }

  const columns = useDarkRulesColumns({
    colorScheme,
    onArchiveRule: handleArchiveRule,
    onToggleRule: handleToggleRule,
  })

  return (
    <ChakraBox data-testid='rulesPage_table' w='100%'>
      <Table
        colorScheme={colorScheme}
        height={`min(${RULES_TABLE_MAX_HEIGHT}, ${tableHeight}px)`}
        maxWidth={RULES_TABLE_MAX_WIDTH}
      >
        <DarkRulesTableHeader
          columns={columns}
          onSortChange={onSortChange}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
        />
        <TableBody isLoading={isLoading} skeletonRowCount={pageSize}>
          {rows.length > 0 ? (
            rows.map((row) => (
              <DarkRulesTableRow
                columns={columns}
                key={row.id}
                onRowClick={openRule}
                row={row}
              />
            ))
          ) : (
            <Flex
              align='center'
              border='1px solid'
              borderColor={getRulesTableToken(colorScheme, 'border.subtle')}
              h={`${RULES_TABLE_ROW_HEIGHT}px`}
              justify='center'
            >
              <Text color={getRulesTableToken(colorScheme, 'text.secondary')}>
                {t('automations:table_no_rules_found')}
              </Text>
            </Flex>
          )}
        </TableBody>
        <TablePagination
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeLabel={t('automations:table_rules_per_page')}
          pageSizeOptions={RULES_TABLE_PAGE_SIZE_OPTIONS}
          total={totalCount}
        />
      </Table>

      {isOpen && (
        <ArchiveRuleModal
          isLoading={isArchiveRuleLoading}
          isOpen={isOpen}
          onClose={onClose}
          onConfirm={handleRemoveRule}
          ruleName={selectedRule?.name}
        />
      )}
    </ChakraBox>
  )
}
